import type { Editor, EditorEvents } from "@tiptap/core";
import {
  ArrowLeft,
  FileText,
  Heading2,
  ImagePlus,
  Link,
  List,
  ListOrdered,
  TextQuote,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { cn } from "~/lib/utils";
import type {
  ReportReferenceDocumentOption,
  ReportReferencePageOption,
} from "./reportReferenceSlashCommand";
import {
  applyReportHeadingCommand,
  convertReportBlockToQuote,
  insertReportReference,
  isReportSlashCommandActive,
  removeReportSlashCommand,
  REPORT_BULLET_LIST_COMMAND_NAME,
  REPORT_HEADING_COMMAND_NAME,
  REPORT_IMAGE_COMMAND_NAME,
  REPORT_NUMBERED_LIST_COMMAND_NAME,
  REPORT_QUOTE_COMMAND_NAME,
  REPORT_REFERENCE_COMMAND_NAME,
  toggleReportBulletList,
  toggleReportNumberedList,
} from "./reportReferenceSlashCommand";

type ReportReferenceSlashMenuProps = {
  editor: Editor;
  documents: ReportReferenceDocumentOption[];
  onSelectImage: () => void;
  onClose: () => void;
};

type SlashMenuStep = "commands" | "documents" | "pages";

type SlashCommandOption = {
  value: string;
  label: string;
  description: string;
  icon: LucideIcon;
  onSelect: () => void;
};

export function ReportReferenceSlashMenu({
  editor,
  documents,
  onSelectImage,
  onClose,
}: ReportReferenceSlashMenuProps) {
  const { formatMessage } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const isSelectingPageRef = useRef(false);
  const isClosedRef = useRef(false);
  const [step, setStep] = useState<SlashMenuStep>("commands");
  const [selectedDocument, setSelectedDocument] =
    useState<ReportReferenceDocumentOption | null>(null);
  const [query, setQuery] = useState("");

  const closeMenu = useCallback(() => {
    if (isClosedRef.current) return;

    isClosedRef.current = true;
    onClose();
  }, [onClose]);

  const handleSelectReferenceCommand = useCallback(() => {
    setStep("documents");
    setQuery("");
  }, []);

  const handleSelectHeadingCommand = useCallback(() => {
    applyReportHeadingCommand(editor);
    closeMenu();
  }, [closeMenu, editor]);

  const handleSelectImageCommand = useCallback(() => {
    if (!removeReportSlashCommand(editor)) {
      closeMenu();
      return;
    }

    onSelectImage();
    closeMenu();
  }, [closeMenu, editor, onSelectImage]);

  const filteredDocuments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return documents;

    return documents.filter((document) =>
      document.searchText.includes(normalizedQuery)
    );
  }, [documents, query]);

  const filteredPages = useMemo(() => {
    if (!selectedDocument) return [];

    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return selectedDocument.pages;

    return selectedDocument.pages.filter((page) =>
      page.searchText.includes(normalizedQuery)
    );
  }, [query, selectedDocument]);

  const handleSelectDocument = (document: ReportReferenceDocumentOption) => {
    setStep("pages");
    setSelectedDocument(document);
    setQuery("");
  };

  const handleSelectQuoteCommand = useCallback(() => {
    convertReportBlockToQuote(editor);
    closeMenu();
  }, [closeMenu, editor]);

  const handleSelectBulletListCommand = useCallback(() => {
    toggleReportBulletList(editor);
    closeMenu();
  }, [closeMenu, editor]);

  const handleSelectNumberedListCommand = useCallback(() => {
    toggleReportNumberedList(editor);
    closeMenu();
  }, [closeMenu, editor]);

  const slashCommands = useMemo<SlashCommandOption[]>(
    () => [
      ...(documents.length > 0
        ? [
            {
              value: REPORT_REFERENCE_COMMAND_NAME,
              label: `/${REPORT_REFERENCE_COMMAND_NAME}`,
              description: formatMessage({
                id: "report.edit.reference.commandDescription",
                defaultMessage: "Insert a source citation",
              }),
              icon: Link,
              onSelect: handleSelectReferenceCommand,
            },
          ]
        : []),
      {
        value: REPORT_QUOTE_COMMAND_NAME,
        label: `/${REPORT_QUOTE_COMMAND_NAME}`,
        description: formatMessage({
          id: "report.edit.reference.quoteCommandDescription",
          defaultMessage: "Convert to block quote",
        }),
        icon: TextQuote,
        onSelect: handleSelectQuoteCommand,
      },
      {
        value: REPORT_HEADING_COMMAND_NAME,
        label: `/${REPORT_HEADING_COMMAND_NAME}`,
        description: formatMessage({
          id: "report.edit.reference.headingCommandDescription",
          defaultMessage: "Turn this line into a heading",
        }),
        icon: Heading2,
        onSelect: handleSelectHeadingCommand,
      },
      {
        value: REPORT_IMAGE_COMMAND_NAME,
        label: `/${REPORT_IMAGE_COMMAND_NAME}`,
        description: formatMessage({
          id: "report.edit.image.commandDescription",
          defaultMessage: "Insert an image",
        }),
        icon: ImagePlus,
        onSelect: handleSelectImageCommand,
      },
      {
        value: REPORT_BULLET_LIST_COMMAND_NAME,
        label: `/${REPORT_BULLET_LIST_COMMAND_NAME}`,
        description: formatMessage({
          id: "report.edit.reference.bulletListCommandDescription",
          defaultMessage: "Create a bullet list",
        }),
        icon: List,
        onSelect: handleSelectBulletListCommand,
      },
      {
        value: REPORT_NUMBERED_LIST_COMMAND_NAME,
        label: `/${REPORT_NUMBERED_LIST_COMMAND_NAME}`,
        description: formatMessage({
          id: "report.edit.reference.numberedListCommandDescription",
          defaultMessage: "Create a numbered list",
        }),
        icon: ListOrdered,
        onSelect: handleSelectNumberedListCommand,
      },
    ],
    [
      documents.length,
      formatMessage,
      handleSelectBulletListCommand,
      handleSelectHeadingCommand,
      handleSelectImageCommand,
      handleSelectNumberedListCommand,
      handleSelectQuoteCommand,
      handleSelectReferenceCommand,
    ]
  );

  const filteredSlashCommands = useMemo(() => {
    const normalizedQuery = query.trim().replace(/^\//, "").toLowerCase();
    if (!normalizedQuery) return slashCommands;

    return slashCommands.filter((command) =>
      command.value.startsWith(normalizedQuery)
    );
  }, [query, slashCommands]);

  const handleSelectPage = (page: ReportReferencePageOption) => {
    if (!isReportSlashCommandActive(editor)) {
      closeMenu();
      return;
    }

    isSelectingPageRef.current = true;
    insertReportReference(editor, page);
    closeMenu();
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  useEffect(() => {
    const closeIfCommandInactive = ({
      editor: activeEditor,
    }: EditorEvents["transaction"] | EditorEvents["selectionUpdate"]) => {
      if (
        !isSelectingPageRef.current &&
        !isReportSlashCommandActive(activeEditor)
      ) {
        closeMenu();
      }
    };

    editor.on("transaction", closeIfCommandInactive);
    editor.on("selectionUpdate", closeIfCommandInactive);

    return () => {
      editor.off("transaction", closeIfCommandInactive);
      editor.off("selectionUpdate", closeIfCommandInactive);
    };
  }, [closeMenu, editor]);

  const inputPlaceholder = (() => {
    if (step === "commands") {
      return formatMessage({
        id: "report.edit.reference.searchCommands",
        defaultMessage: "Search commands...",
      });
    }

    if (step === "documents") {
      return formatMessage({
        id: "report.edit.reference.searchDocuments",
        defaultMessage: "Search documents...",
      });
    }

    return formatMessage({
      id: "report.edit.reference.searchPages",
      defaultMessage: "Search pages...",
    });
  })();

  const groupHeading = (() => {
    if (step === "commands") {
      return formatMessage({
        id: "report.edit.reference.slashCommands",
        defaultMessage: "Slash Commands",
      });
    }

    if (step === "documents") {
      return formatMessage({
        id: "report.edit.reference.documents",
        defaultMessage: "Documents",
      });
    }

    return selectedDocument?.title ?? "";
  })();

  return (
    <div className="absolute left-3 top-14 z-50 w-[min(28rem,calc(100%-1.5rem))] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg">
      <Command shouldFilter={false} loop>
        <CommandInput
          ref={inputRef}
          value={query}
          onValueChange={setQuery}
          placeholder={inputPlaceholder}
        />
        <CommandList className="max-h-72">
          <CommandEmpty>
            <FormattedMessage
              id="report.edit.reference.empty"
              defaultMessage="No references found."
            />
          </CommandEmpty>
          <CommandGroup heading={groupHeading}>
            {step === "commands" &&
              filteredSlashCommands.map((command) => {
                const Icon = command.icon;

                return (
                  <CommandItem
                    key={command.value}
                    value={command.value}
                    onSelect={command.onSelect}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{command.label}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {command.description}
                      </span>
                    </span>
                  </CommandItem>
                );
              })}
            {step === "pages" && (
              <CommandItem
                value="back-to-documents"
                onSelect={() => {
                  setStep("documents");
                  setSelectedDocument(null);
                  setQuery("");
                }}
              >
                <ArrowLeft className="h-4 w-4" />
                <FormattedMessage
                  id="report.edit.reference.backToDocuments"
                  defaultMessage="Back to Documents"
                />
              </CommandItem>
            )}
            {step === "pages" && selectedDocument
              ? filteredPages.map((page) => (
                  <CommandItem
                    key={page.pageId}
                    value={page.pageId}
                    onSelect={() => handleSelectPage(page)}
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{page.label}</span>
                      {page.batesNumber && (
                        <span className="truncate text-xs text-muted-foreground">
                          <FormattedMessage
                            id="report.edit.reference.pageWithBates"
                            defaultMessage="Reference page"
                          />
                        </span>
                      )}
                    </span>
                  </CommandItem>
                ))
              : step === "documents" &&
                filteredDocuments.map((document) => (
                  <CommandItem
                    key={document.sectionId}
                    value={document.sectionId}
                    onSelect={() => handleSelectDocument(document)}
                  >
                    <FileText className="h-4 w-4" />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{document.title}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {document.categoryName}
                      </span>
                    </span>
                    <span
                      className={cn(
                        "ml-auto shrink-0 rounded bg-muted px-1.5 py-0.5",
                        "text-xs text-muted-foreground"
                      )}
                    >
                      <FormattedMessage
                        id="report.edit.reference.pageCount"
                        defaultMessage="{count, plural, one {# page} other {# pages}}"
                        values={{ count: document.pages.length }}
                      />
                    </span>
                  </CommandItem>
                ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}
